import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { v4 as uuid } from 'uuid';

export const typeDefs = `
  input CsatAnswerInput {
    score: String
    comment: String
    metadata: String
  }

  type CsatAnswer {
    uuid: ID!
    score: Int
    comment: String
    metadata: String
  }

  type Mutation {
    createAnswer(input: CsatAnswerInput): CsatAnswer
    updateAnswer(id: ID!, input: CsatAnswerInput): CsatAnswer
  }
`;

export const createMutation = `
  mutation Mutation($input: CsatAnswerInput!) {
    createAnswer(input: $input) {
      uuid
      score
      comment
      metadata
    }
  }
`;

export const updateMutation = `
  mutation Mutation($input: CsatAnswerInput!, $answerId: ID!) {
      updateAnswer(id: $answerId, input: $input) {
          uuid
          score
          comment
          metadata
      }
  }
`;

export class SebCsat extends LitElement {
  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static override styles = css`
    :host {
      font-family: 'Segoe UI', sans-serif;
      display: block;
      box-sizing: border-box;
      text-align: center;
    }

    * {
      font-family: 'Segoe UI', sans-serif;
    }

    :host([show='false']) .seb-component-csat-root {
      visibility: hidden;
      bottom: -100%;
      top: 100%;
      height: 0;
      width: 0;
      overflow: hidden;
    }

    :host([show]) .seb-component-csat-root {
      display: block;
      justify-content: center;
      align-content: center;
      flex-direction: column;
      pointer-events: none;
      z-index: 99999;
      transition: visibility 1s;
      visibility: visible;
    }

    :host([type='popup']) .seb-component-csat-root {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
    }

    .seb-component-csat-container {
      position: relative;
      display: block;
      width: 100%;
      height: auto;
      padding: 20px;
      max-width: 600px;
      max-height: 500px;
      border: 1px solid #e0e0e0;
      margin: auto;
      z-index: 99999;
      background-color: white;
      pointer-events: auto;
      border-radius: 4px;
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
      transition: all 0.3s ease-in-out;
      box-sizing: border-box;
    }

    :host([type='popup']) .seb-component-csat-backdrop {
      display: none;
    }

    :host([position='bottom']) .seb-component-csat-container {
      margin-bottom: 10px;
    }

    :host([type='popup']) .seb-component-csat-container {
      margin-bottom: 0;
    }

    @media screen and (max-width: 768px) {
      .seb-component-csat-container {
        max-width: 100%;
        max-height: 100%;
      }
    }

    .seb-component-csat-comment-container {
      display: flex;
      justify-content: flex-start;
      flex-direction: column;
      align-items: flex-start;
    }

    .seb-component-csat-close-button {
      position: absolute;
      top: 10px;
      right: 10px;
      cursor: pointer;
      border: none;
      color: #6a737d;
      font-size: 1.5rem;
      font-weight: 600;
      line-height: 1;
      text-shadow: 0 1px 0 #fff;
      opacity: 0.3;
      width: 30px;
      display: flex;
      justify-content: center;
      align-content: center;
      padding: 0;
      margin: 0;
      background-color: transparent;
    }

    .seb-component-csat-close-button:hover {
      filter: brightness(0.4);
    }

    .seb-component-csat-backdrop {
      background-color: rgba(0, 0, 0, 0.5);
      position: absolute;
      height: 100%;
      width: 100%;
      left: 0;
      top: 0;
    }

    .seb-component-csat-score-container {
      padding: 10px 0 0 0;
      width: 100%;
      max-width: 600px;
      margin: auto;
    }

    .seb-component-csat-score-title,
    .seb-component-csat-comment-title {
      font-size: 1.2rem;
      padding-bottom: 10px;
      font-family: 'Segoe UI', sans-serif;
      font-weight: 700;
      color: #242a30;
      width: 100%;
    }

    .seb-component-csat-comment-title {
      font-family: Segoe UI, sans-serif;
      font-size: 1.2rem;
      font-style: normal;
      font-weight: 400;
      color: #666666;
    }

    .seb-component-csat-score-list {
      list-style: none;
      display: flex;
      justify-content: space-between;
      align-content: center;
      gap: 10px;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(1)
      .seb-component-csat-score-button {
      background-color: red;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(1)
      .seb-component-csat-score-button:focus,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(1)
      .seb-component-csat-score-button.active,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(1)
      .seb-component-csat-score-button.active.loading:after {
      outline: 2px solid red;
      border-color: red;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(2)
      .seb-component-csat-score-button {
      background-color: orangered;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(2)
      .seb-component-csat-score-button:focus,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(2)
      .seb-component-csat-score-button.active,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(2)
      .seb-component-csat-score-button.active.loading:after {
      outline: 2px solid orangered;
      border-color: orangered;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(3)
      .seb-component-csat-score-button {
      background-color: darkorange;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(3)
      .seb-component-csat-score-button:focus,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(3)
      .seb-component-csat-score-button.active,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(3)
      .seb-component-csat-score-button.active.loading:after {
      outline: 2px solid darkorange;
      border-color: darkorange;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(4)
      .seb-component-csat-score-button {
      background-color: forestgreen;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(4)
      .seb-component-csat-score-button:focus,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(4)
      .seb-component-csat-score-button.active,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(4)
      .seb-component-csat-score-button.active.loading:after {
      outline: 2px solid forestgreen;
      border-color: forestgreen;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(5)
      .seb-component-csat-score-button {
      background-color: green;
    }

    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(5)
      .seb-component-csat-score-button:focus,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(5)
      .seb-component-csat-score-button.active,
    .seb-component-csat-score-list
      .seb-component-csat-score-item:nth-child(5)
      .seb-component-csat-score-button.active.loading:after {
      outline: 2px solid green;
      border-color: green;
    }

    .seb-component-csat-score-button {
      display: flex;
      justify-content: center;
      align-content: center;
      align-items: center;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      border: 1px solid #e1e4e8;
      background-color: white;
      cursor: pointer;
      color: white;
      font-weight: bold;
      transition: all 0.2s ease-in-out;
      position: relative;
    }

    .seb-component-csat-score-button:disabled.loading.active {
      cursor: not-allowed;
    }

    .seb-component-csat-score-button:focus {
      outline-offset: 2px;
    }

    .seb-component-csat-score-button.active:not(.loading) {
      outline-offset: 1px;
      outline-width: 4px !important;
    }

    .seb-component-csat-score-button.active.loading {
      outline: none !important;
    }

    .seb-component-csat-score-button.active.loading:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 4px solid #0b5ed7;
      border-top-color: white !important;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .seb-component-csat-score-button:hover {
      filter: brightness(1.2);
    }

    .seb-component-csat-score-list,
    .seb-component-csat-score-labels {
      margin: 0;
      padding: 10px 20px;
      font-size: 12px;
      font-family: 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
      color: #707478;
    }

    .seb-component-csat-score-labels {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }

    .seb-component-csat-score-labels .seb-component-csat-score-label {
      text-align: center;
      width: 50px;
    }

    .seb-component-csat-divider {
      display: block;
      width: 100%;
      height: 1px;
      background-color: #e1e4e8;
      margin: 5px auto;
      max-width: 600px;
    }

    .seb-component-csat-comment-input-group {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      padding: 0;
      gap: 20px;
      position: relative;
      box-sizing: border-box;
      max-width: 600px;
      margin: auto;
    }

    .seb-component-csat-comment-textarea {
      display: block;
      width: 100%;
      max-width: 600px;
      height: auto;
      border: 1px solid #e1e4e8;
      border-radius: 4px;
      resize: none;
      flex: 4;
      margin-bottom: 5px;
      box-sizing: border-box;
      padding: 5px 60px 5px 5px;
    }

    .seb-component-csat-comment-textarea::placeholder {
      font-size: 1rem;
    }

    .seb-component-csat-comment-textarea:focus {
      border: 1px solid #0b5ed7;
      outline: none;
    }

    .seb-component-csat-comment-button {
      color: #0b5ed7;
      background-color: transparent;
      border: none;
      flex: 1;
      max-width: 50px;
      max-height: 50px;
      position: absolute;
      right: 25px;
      top: 50%;
      transform: translateY(-50%);
    }

    .seb-component-csat-comment-button svg {
      pointer-events: none;
    }

    .seb-component-csat-comment-button:hover {
      filter: brightness(1.2);
      cursor: pointer;
    }

    .seb-component-csat-comment-button:focus svg path {
      stroke: #0b5ed7;
      stroke-width: 1px;
      stroke-dashoffset: 2px;
      stroke-linejoin: round;
    }

    .seb-component-csat-comment-button:disabled {
      cursor: not-allowed;
      color: grey;
    }

    .seb-component-csat-comment-characters-counter {
      font-size: 0.9rem;
      color: #6a737d;
      font-weight: 600;
      line-height: 1;
      text-shadow: 0 1px 0 #fff;
      width: 100%;
      max-width: 600px;
      text-align: start;
      margin: auto;
    }

    .seb-component-csat-comment-characters-counter-value {
      font-weight: bold;
      margin-left: 4px;
    }

    .seb-component-csat-comment-characters-counter-value.low {
      color: forestgreen;
    }

    .seb-component-csat-comment-characters-counter-value.medium {
      color: darkorange;
    }

    .seb-component-csat-comment-characters-counter-value.high {
      color: orangered;
    }

    .seb-component-csat-comment-characters-counter-value.over {
      color: red;
    }

    @media screen and (max-width: 400px) {
      .seb-component-csat-score-button {
        width: 30px;
        height: 30px;
      }

      .seb-component-csat-score-button.active.loading:after {
        width: 20px;
        height: 20px;
      }
    }
  `;

  // region Properties
  @property({ type: Boolean, reflect: true })
  show = false;

  @property({ type: String })
  apiUrl = 'https://smo-graphapi-dev.azurewebsites.net/api/graphql';

  @property({ type: String })
  canClose = 'false';

  @property({ type: String })
  closeText = 'Fechar';

  @property({ type: String })
  scoreTitle = 'O quanto você está satisfeito com o nosso produto?';

  @property({ type: String })
  lowScoreLabel = 'Pouco Satisfeito';

  @property({ type: String })
  highScoreLabel = 'Muito Satisfeito';

  @property({ type: String })
  commentLabel = 'Em poucas palavras, descreva o que motivou a sua nota.';

  @property({ type: String })
  commentPlaceholder = 'Escreva aqui sua resposta';

  @property({ type: String })
  commentButtonLabel = 'Enviar';

  @property({ type: String })
  commentRemainingCharactersLabel = 'Caracteres restantes';

  @property({ type: Number })
  commentMaxSize = 255;

  @property({ type: Number })
  score: number | null = null;

  @property({ type: String })
  comment = '';

  @property({ type: Boolean })
  isSaving = false;

  @property({ type: Boolean })
  isSavingScore = false;

  @property({ type: Number })
  lowCommentSize = this.commentMaxSize * 0.3;

  @property({ type: Number })
  mediumCommentSize = this.commentMaxSize * 0.7;

  @property({ type: Number })
  highCommentSize = this.commentMaxSize;

  @property({ type: String })
  commentSizeClass = this._getCommentSizeClass();

  @property({ type: String })
  answerId = uuid();

  @property({ type: String })
  context = null;

  @property()
  client = null;

  @property()
  createMutation = null;

  @property()
  updateMutation = null;

  // endregion Properties

  @query('#seb-component-csat-comment-textarea')
  _textarea: HTMLTextAreaElement | undefined;

  override connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    // eslint-disable-next-line wc/require-listener-teardown
    window.addEventListener('keydown', (e: KeyboardEvent) =>
      this._handleKeydown(e)
    );
  }

  override render() {
    return html`
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="seb-component-csat-title"
        aria-hidden=${!this.show}
        class="seb-component-csat-root"
        ?hidden=${!this.show}
      >
        <span class="seb-component-csat-backdrop"></span>

        <div class="seb-component-csat-container">
          ${this.canClose === 'true'
            ? html`
                <button
                  aria-label=${this.closeText}
                  data-dismiss="modal"
                  title=${this.closeText}
                  id="seb-component-csat-close-button"
                  class="seb-component-csat-close-button"
                  @click=${this._handleClose}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              `
            : ``}

          <div class="seb-component-csat-score-container">
            <h1
              id="seb-component-csat-title"
              class="seb-component-csat-score-title"
            >
              ${this.scoreTitle}
            </h1>

            <ul class="seb-component-csat-score-list">
              ${Array.from({ length: 5 }, (_, i) => i + 1).map(
                (s: number) => html`
                  <li class="seb-component-csat-score-item">
                    <button
                      class=${`seb-component-csat-score-button ${
                        s === this.score ? 'active' : ''
                      } ${this.isSavingScore ? 'loading' : ''}`}
                      @click=${this._handleScoreClick(s)}
                      data-value=${s}
                      ?disabled=${this.isSaving ||
                      (this.isSavingScore && s === this.score) ||
                      s === this.score}
                      type="button"
                    >
                      ${s}
                    </button>
                  </li>
                `
              )}
            </ul>

            <div class="seb-component-csat-score-labels">
              <span class="seb-component-csat-score-label" aria-hidden="true">
                ${this.lowScoreLabel
                  .split(' ')
                  .map(word => html` <span>${word} <br /></span> `)}
              </span>
              <span class="seb-component-csat-score-label" aria-hidden="true">
                ${this.highScoreLabel
                  .split(' ')
                  .map(word => html` <span>${word} <br /></span> `)}
              </span>
            </div>
          </div>

          ${this.score !== null
            ? html`
                <span class="seb-component-csat-divider"></span>

                <div class="seb-component-csat-comment-container">
                  <label
                    class="seb-component-csat-comment-title"
                    for="seb-component-csat-comment-textarea"
                  >
                    ${this.commentLabel}
                  </label>

                  <div class="seb-component-csat-comment-input-group">
                    <textarea
                      maxlength=${this.commentMaxSize}
                      rows="5"
                      name="comment"
                      @input=${this._handleCommentChange}
                      id="seb-component-csat-comment-textarea"
                      class="seb-component-csat-comment-textarea"
                      placeholder=${this.commentPlaceholder}
                    ></textarea>
                    <button
                      class="seb-component-csat-comment-button"
                      @click=${this._handleCommentSubmit}
                      ?disabled=${this.isSaving ||
                      this.comment.length === 0 ||
                      this.score === null}
                      aria-label=${this.commentButtonLabel}
                    >
                      ${this._renderCommentIcon()}
                    </button>
                  </div>
                  <span
                    class="seb-component-csat-comment-characters-counter"
                    aria-hidden="true"
                  >
                    ${this.commentRemainingCharactersLabel}:
                    <span
                      class=${`seb-component-csat-comment-characters-counter-value ${this.commentSizeClass}`}
                    >
                      ${this.commentMaxSize - this.comment.length}
                    </span>
                  </span>
                </div>
              `
            : ''}
        </div>
      </div>
    `;
  }

  private _handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.show = false;
      this.requestUpdate('show');
    }
  }

  private _getCommentSizeClass() {
    if (this.comment.length === 0) {
      return '';
    }
    if (this.comment.length <= this.lowCommentSize) {
      return 'low';
    }
    if (this.comment.length <= this.mediumCommentSize) {
      return 'medium';
    }
    if (this.comment.length < this.highCommentSize) {
      return 'high';
    }
    if (this.comment.length === this.commentMaxSize) {
      return 'over';
    }

    return '';
  }

  private _renderCommentIcon() {
    return this.isSaving
      ? html`
          <svg
            class="seb-component-csat-comment-button-loading"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid"
            width="24"
            height="24"
          >
            <circle
              cx="50"
              cy="50"
              fill="none"
              stroke="currentColor"
              stroke-width="10"
              r="35"
              stroke-dasharray="164.93361431346415 56.97787143782138"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                repeatCount="indefinite"
                dur="1s"
                values="0 50 50;360 50 50"
                keyTimes="0;1"
              ></animateTransform>
            </circle>
          </svg>
        `
      : html`
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
          >
            <title>${this.commentButtonLabel}</title>
            <path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
          </svg>
        `;
  }

  private _handleScoreClick(score: number) {
    return () => {
      this.score = score;
      this.isSavingScore = true;

      this._saveAnswer()
        .then(resp => {
          if (resp && !this.answerId) {
            this.answerId = resp.data.createAnswer.uuid;
          }
        })
        .finally(() => {
          this.isSavingScore = false;
        });

      this.dispatchEvent(
        new CustomEvent('score-clicked', {
          detail: {
            score,
          },
        })
      );
    };
  }

  private _handleClose() {
    this.show = false;
    this.score = null;
    this.answerId = '';
    this.comment = '';
    if (this._textarea) {
      this._textarea.value = '';
    }
    this.dispatchEvent(new CustomEvent('closed'));
  }

  private _handleCommentChange(e: InputEvent) {
    const input = e.target as HTMLTextAreaElement;
    this.comment = input.value || '';
    this.commentSizeClass = this._getCommentSizeClass();
  }

  private _handleCommentSubmit() {
    const data = {
      score: this.score,
      comment: this.comment,
      context: this.context,
    };
    this.isSaving = true;

    this._saveAnswer()
      .then(resp => {
        if (resp && !this.answerId) {
          this.answerId = resp.data.createAnswer.uuid;
        }
      })
      .finally(() => {
        this.isSaving = false;
        this.score = null;
        this.answerId = '';
        this.comment = '';
        if (this._textarea) {
          this._textarea.value = '';
        }
        this.show = false;
      });

    this.dispatchEvent(
      new CustomEvent('submit', {
        detail: data,
      })
    );
  }

  private async _saveAnswer() {
    if (this.client !== null) {
      // @ts-ignore
      return this.client.mutate({
        mutation: this.answerId ? this.updateMutation : this.createMutation,
        variables: {
          answerId: this.answerId,
          input: {
            score: `${this.score}`,
            comment: `${this.comment}`,
            metadata: `${this.context}`,
          },
        },
      });
    }

    return null;
  }
}
